// =============================================================================
// screens/MarketsScreen.tsx — the Markets tab (Triage P0 + IMG_5879 adoption).
//
// FIXES vs the old Expo markets screen (IMG_5848):
//   - P0 sort: "Market Cap" now sorts by REAL market cap descending (BTC/ETH on
//     top) via services/markets sortTokens(); array order is never trusted.
//     (Root cause: /crypto/prices returns alphabetical rows and the old client
//     used array index as rank — see services/markets.ts header.)
//   - P1 icons: real coin logos from /market/tokens (CoinIcon w/ monogram fallback).
//   - P2 palette: the dark-navy stat slabs are now light glass cards on the
//     lavender system (glass contract), matching the rest of the app.
//   - IMG_5879: per-row mini sparklines (components/markets/Sparkline) fed by an
//     honest interpolation of the real 1h/24h/7d change anchors.
//
// v1 crypto policy: market DATA is viewable (info-only). Tapping a row opens
// CoinDetail (info-only). No transactional surface lives here.
//
// REGISTRATION (orchestrator): drop into the Tabs "Markets" slot —
//   <Tab.Screen name="Markets" component={MarketsScreen} />
// =============================================================================
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  RefreshControl,
  ActivityIndicator,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons, IoniconName } from '../components/ui/Icon';
import Asset3D from '../components/ui/Asset3D';
import ScreenHeading from '../components/ui/ScreenHeading';
import { screenBottomInset } from '../components/ui/Screen';
import ParticleBackground from '../components/ui/ParticleBackground';
import ScreenTip from '../components/ui/ScreenTip';
import { GlassCard, GlassPill, GlassPillGroup } from '../components/glass';
import Sparkline from '../components/markets/Sparkline';
import CoinIcon from '../components/markets/CoinIcon';
import {
  MarketToken,
  MarketOverview,
  SortKey,
  SORT_KEYS,
  fetchMarketTokens,
  fetchMarketOverview,
  sortTokens,
  approxSeries,
  formatPrice,
  formatLargeNumber,
} from '../services/markets';
import { tabularNums, ticker } from '../lib/format';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  shadowTokens,
  colorTokens,
  layout as layoutTokens,
  getLayout,
} from '../constants/theme';
import { glassTokens } from '../components/glass';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// Refresh cadence while the tab is focused (stops on blur — no background drain).
const POLL_MS = 30000;

// Directional change-chip tints derived from the brand palette (no ad-hoc rgba
// scattered inline).
const TINT = {
  successSoft: 'rgba(45,181,93,0.12)',
  errorSoft: 'rgba(255,71,87,0.10)',
} as const;

// ---- Stat card (light glass — replaces the navy slabs of IMG_5848) -----------
function StatCard({ label, value, icon }: { label: string; value: string; icon: IoniconName }) {
  return (
    <GlassCard style={styles.statCard} contentStyle={styles.statContent} padding={spacing.md}>
      <LinearGradient
        colors={colors.gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.statIcon}
      >
        <Ionicons name={icon} size={13} color={colors.white} />
      </LinearGradient>
      <View style={styles.statTextWrap}>
        <Text style={styles.statLabel} numberOfLines={1}>{label}</Text>
        <Text style={styles.statValue} numberOfLines={1} adjustsFontSizeToFit>{value}</Text>
      </View>
    </GlassCard>
  );
}

// ---- Token row (IMG_5879: logo · name · sparkline · price/%) ------------------
function TokenRow({ token, rank, onPress }: { token: MarketToken; rank: number; onPress: () => void }) {
  const change = token.change24h ?? 0;
  const up = change >= 0;
  const series = useMemo(() => approxSeries(token), [token]);
  return (
    <GlassCard
      style={styles.row}
      contentStyle={styles.rowContent}
      padding={spacing.md}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${token.name}, ${formatPrice(token.price)}, ${up ? 'up' : 'down'} ${Math.abs(change).toFixed(2)} percent in 24 hours`}
    >
      <Text style={styles.rank}>{rank}</Text>
      <CoinIcon symbol={token.symbol} image={token.image} size={36} />
      <View style={styles.rowInfo}>
        <Text style={styles.rowName} numberOfLines={1}>{token.name}</Text>
        <Text style={styles.rowSymbol} numberOfLines={1}>
          {ticker(token.symbol)}
          {token.marketCap ? `  ·  ${formatLargeNumber(token.marketCap)}` : ''}
        </Text>
      </View>
      <View style={styles.rowSpark}>
        <Sparkline data={series} width={58} height={24} />
      </View>
      <View style={styles.rowPriceWrap}>
        <Text style={[styles.rowPrice, tabularNums]} numberOfLines={1}>{formatPrice(token.price)}</Text>
        <View style={[styles.changeBadge, { backgroundColor: up ? TINT.successSoft : TINT.errorSoft }]}>
          <Ionicons name={up ? 'caret-up' : 'caret-down'} size={9} color={up ? colors.success : colors.error} />
          <Text style={[styles.changeText, tabularNums, { color: up ? colors.success : colors.error }]}>
            {Math.abs(change).toFixed(2)}%
          </Text>
        </View>
      </View>
    </GlassCard>
  );
}

export default function MarketsScreen() {
  const navigation = useNavigation<Nav>();
  // This tab owns its OWN translucent in-content header (like Home/Communities),
  // so the ParticleBackground bleeds full-bleed under the status bar/notch. Any
  // native "Markets" strip registered by the stack would render OPAQUE above the
  // animated layer (the misaligned top gap) — suppress it (skill §12).
  const insets = useSafeAreaInsets();
  // Responsive: cap width + center content on iPad/desktop; phone unchanged.
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle =
    layout !== 'phone'
      ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const }
      : undefined;
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);
  const [tokens, setTokens] = useState<MarketToken[]>([]);
  const [overview, setOverview] = useState<MarketOverview>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeSort, setActiveSort] = useState<SortKey>('Market Cap');

  const load = useCallback(async () => {
    const [tokenRes, ov] = await Promise.all([fetchMarketTokens(), fetchMarketOverview()]);
    if (tokenRes.tokens.length > 0) {
      setTokens(tokenRes.tokens);
      setLoadError(null);
    } else if (tokenRes.error) {
      // Keep stale rows on a transient refresh failure; only surface the error
      // state when we have nothing at all to show.
      setLoadError(tokenRes.error);
    }
    setOverview((prev) => (Object.keys(ov).length > 0 ? ov : prev));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live-ish prices while focused (matches the old screen's polling behavior).
  useFocusEffect(
    useCallback(() => {
      const id = setInterval(() => { load(); }, POLL_MS);
      return () => clearInterval(id);
    }, [load])
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  // THE P0 FIX: rank by the selected metric, never by fetch order.
  const sorted = useMemo(() => sortTokens(tokens, activeSort), [tokens, activeSort]);

  const listHeader = (
    <View>
      {/* Stats grid — light glass, 2×2 */}
      <View style={styles.statsGrid}>
        <StatCard label="Market Cap" value={formatLargeNumber(overview.totalMarketCap)} icon="globe-outline" />
        <StatCard label="24h Volume" value={formatLargeNumber(overview.totalVolume24h)} icon="pulse-outline" />
        <StatCard label="BTC Dom" value={overview.btcDominance != null ? overview.btcDominance.toFixed(1) + '%' : '—'} icon="logo-bitcoin" />
        <StatCard label="ETH Dom" value={overview.ethDominance != null ? overview.ethDominance.toFixed(1) + '%' : '—'} icon="diamond-outline" />
      </View>

      {/* Sort chips */}
      <GlassPillGroup style={styles.sortRow}>
        {SORT_KEYS.map((key) => (
          <GlassPill
            key={key}
            label={key}
            active={activeSort === key}
            onPress={() => setActiveSort(key)}
            accessibilityLabel={`Sort by ${key}`}
          />
        ))}
      </GlassPillGroup>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Living animated background (design-system): full-bleed aurora under the
          status bar — replaces the flat lavender/blobs so Markets reads as a deep,
          premium glass surface, matching Home/Communities. */}
      <ParticleBackground theme="light" count={5} />

      {/* In-content translucent header (chrome bleeds over the particle bg — no
          opaque native strip). Title = large display type with tightened tracking
          (skill §15), search action mirrors the old Header `right` slot. */}
      <View style={[styles.header, containerStyle, { paddingTop: insets.top + spacing.sm }]}>
        <ScreenHeading title="Markets" />
        <TouchableOpacity
          onPress={() => navigation.navigate('Search')}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Search"
          style={styles.headerAction}
        >
          <Ionicons name="search-outline" size={22} color={colors.ink} />
        </TouchableOpacity>
      </View>

      <View style={containerStyle}>
        <ScreenTip
          tipKey="markets"
          title="Markets"
          text="Live crypto prices, ranked by real market cap. Tap any coin for charts, links and its CRYB community."
        />
      </View>

      {loading ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={sorted}
          keyExtractor={(item, index) => item.id || item.symbol || String(index)}
          renderItem={({ item, index }) => (
            <TokenRow
              token={item}
              rank={index + 1}
              onPress={() => navigation.navigate('CoinDetail', { symbol: item.symbol, name: item.name, image: item.image })}
            />
          )}
          ListHeaderComponent={listHeader}
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <Asset3D name={loadError ? 'error' : 'chart'} size={120} style={{ marginBottom: spacing.sm }} />
              <Text style={styles.emptyText}>{loadError ? "Couldn't load markets" : 'No market data'}</Text>
              <Text style={styles.emptySubtext}>
                {loadError ? 'Check your connection and try again.' : 'Pull down to refresh.'}
              </Text>
              <TouchableOpacity
                style={styles.retryBtn}
                onPress={onRefresh}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Retry loading market data"
              >
                <Ionicons name="refresh-outline" size={15} color={colors.white} />
                <Text style={styles.retryText}>Retry</Text>
              </TouchableOpacity>
            </View>
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, containerStyle]}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  // In-content translucent header (bleeds over the particle bg, no opaque strip).
  // Title = display type: h1 size/leading + bold weight + tightened tracking
  // (skill §15 — size-specific tracking, tightened for large display text vs the
  // generic 17px native title).
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: layoutTokens.gutter,
    paddingBottom: spacing.xs,
  },
  headerTitle: {
    ...typographyScale.h1,
    ...fonts.bold,
    color: colors.ink,
    letterSpacing: -0.6,
  },
  headerAction: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: -spacing.xs,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  statCard: {
    flexBasis: '47%',
    flexGrow: 1,
    ...shadowTokens.bar,
  },
  statContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  statIcon: {
    width: 28,
    height: 28,
    borderRadius: radius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statTextWrap: { flex: 1 },
  statLabel: {
    ...fonts.semibold,
    fontSize: 10.5,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: colors.muted,
  },
  statValue: {
    ...typographyScale.h3,
    ...fonts.bold,
    ...tabularNums,
    color: colorTokens.light.text,
    letterSpacing: -0.3,
    marginTop: 2,
  },
  sortRow: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  row: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
    ...shadowTokens.card,
  },
  rowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rank: {
    width: 20,
    ...fonts.semibold,
    fontSize: 12,
    color: colors.muted,
    textAlign: 'center',
  },
  rowInfo: { flex: 1, minWidth: 0 },
  rowName: {
    ...fonts.semibold,
    fontSize: 15,
    letterSpacing: -0.2,
    color: colors.ink,
  },
  rowSymbol: {
    ...fonts.medium,
    fontSize: 11,
    color: colors.muted,
    marginTop: 1,
  },
  rowSpark: { marginHorizontal: spacing.xs },
  rowPriceWrap: { alignItems: 'flex-end', minWidth: 78 },
  rowPrice: {
    ...fonts.semibold,
    fontSize: 15,
    letterSpacing: -0.2,
    color: colors.ink,
  },
  changeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: radius.sm,
    marginTop: 3,
  },
  changeText: {
    ...fonts.semibold,
    fontSize: 11,
  },
  listContent: {
    paddingBottom: screenBottomInset,
  },
  loadingWrap: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  emptyWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 72,
    gap: spacing.xs,
  },
  emptyText: {
    ...fonts.semibold,
    fontSize: 16,
    color: colors.ink,
  },
  emptySubtext: {
    ...fonts.regular,
    fontSize: 13,
    color: colors.muted,
  },
  retryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    marginTop: spacing.md,
    minHeight: 44,
  },
  retryText: {
    ...fonts.semibold,
    fontSize: 14,
    color: colors.white,
  },
});
